import { useMemo } from 'react';

import { type ColumnDef } from '@tanstack/react-table';
import { PencilIcon } from 'lucide-react';

import { ActionButton, CellFallback, ColumnHeader, DeleteButton } from '@/_containers';

import type { PaymentMethodFieldFormRowSchema } from '../../schemas/payment-method-field-query-schema';

type UseDefineColumnsProps = {
  isEditable: boolean;
  onEdit: (row: PaymentMethodFieldFormRowSchema) => void;
  onDelete: (id: number) => void;
};

export function useDefineColumns({ isEditable, onEdit, onDelete }: UseDefineColumnsProps) {
  return useMemo(() => {
    const columns: ColumnDef<PaymentMethodFieldFormRowSchema>[] = [
      {
        accessorKey: 'label',
        meta: { name: 'Label' },
        header: ({ column }) => <ColumnHeader column={column} />,
        cell: ({ row }) => <CellFallback text={row.original.label} />,
        enableSorting: false,
      },
      {
        accessorKey: 'type',
        meta: { name: 'Type', className: 'w-28' },
        header: ({ column }) => <ColumnHeader column={column} />,
        cell: ({ row }) => <CellFallback text={row.original.type === 'select' ? 'Select' : 'Text'} />,
        enableSorting: false,
      },
      {
        accessorKey: 'is_required',
        meta: { name: 'Required', className: 'w-24' },
        header: ({ column }) => <ColumnHeader column={column} align="center" />,
        cell: ({ row }) => <CellFallback align="center" text={row.original.is_required ? 'Yes' : 'No'} />,
        enableSorting: false,
      },
      {
        accessorKey: 'options',
        meta: { name: 'Options' },
        header: ({ column }) => <ColumnHeader column={column} />,
        cell: ({ row }) => {
          const raw = row.original.options?.trim() ?? '';
          const text = row.original.type === 'select' && raw ? row.original.options : '—';
          return <CellFallback text={text} />;
        },
        enableSorting: false,
      },
    ];

    if (isEditable) {
      columns.push({
        id: 'actions',
        meta: { name: '', className: 'w-28 min-w-24' },
        header: ({ column }) => <ColumnHeader column={column} />,
        cell: ({ row }) => {
          const id = row.original.id;
          return (
            <div className="flex items-center justify-end gap-0.5">
              <ActionButton
                aria-label="Edit field"
                title="Edit field"
                onClick={() => onEdit(row.original)}
                icon={PencilIcon}
              />
              <DeleteButton
                type="icon"
                size="sm"
                title="Delete field?"
                text="Are you sure you want to delete this payment method field?"
                yesButton="Yes, delete"
                aria-label="delete-payment-method-field"
                tooltipTitle="Delete field"
                confirmDelete
                callback={() => {
                  onDelete(id);
                }}
              />
            </div>
          );
        },
        enableSorting: false,
      });
    }

    return columns;
  }, [isEditable, onEdit, onDelete]);
}
